import {Component, effect, EffectRef, inject, input, InputSignal, linkedSignal, output, OutputEmitterRef, signal, ViewEncapsulation, WritableSignal} from '@angular/core';
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {DialogService} from "../../../shared/services/dialog.service";
import {FormBuilder, FormGroup} from "@angular/forms";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {RouterService} from "../../../shared/services/router.service";
import {TYPE} from "../../../shared/types/types";
import {take} from "rxjs";
import {ApiService} from "../../../shared/services/api.service";
import {NavService} from "../../../shared/services/nav.service";
import {CustomerMenuResource} from '../../../shared/types/settings-types/customerMenuResource';
import {CheckBoxModule} from "@syncfusion/ej2-angular-buttons";
import {SettingsMenuItemType} from '../../../shared/types/settings-types/settingsMenuItemType';
import {SettingsMenuCustomerRequest} from '../../../shared/types/settings-types/settingsMenuCustomerRequest';
import {SettingsMenuCustomerRequestItemsInner} from '../../../shared/types/settings-types/settingsMenuCustomerRequestItemsInner';
import {UserMenuResource} from '../../../shared/types/settings-types/userMenuResource';
import {UserMenuRequest} from '../../../shared/types/settings-types/userMenuRequest';
import {UserMenuItemRequest} from '../../../shared/types/settings-types/userMenuItemRequest';
import {ProfileService} from '../../../shared/services/profile.service';

@Component({
    selector: 'app-dialog-home-icons',
    imports: [
    DialogModule,
    HeadlineComponent,
    TooltipComponent,
    TranslatePipe,
    TooltipModule,
    IconBadgeComponent,
    FormButtonComponent,
    CheckBoxModule
],
    templateUrl: './dialog-home-icons.component.html',
    styleUrl: './dialog-home-icons.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class DialogHomeIconsComponent {
  public dialogService: DialogService = inject(DialogService);
  public routerService: RouterService = inject(RouterService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private navService: NavService = inject(NavService);
  private profileService: ProfileService = inject(ProfileService);

  public visible: InputSignal<boolean> = input(false);
  public isAdminSettings: InputSignal<boolean> = input(false);
  public visibleChange: OutputEmitterRef<boolean> = output<boolean>();

  public iconsHome: TYPE.HomeIcon[] = [];
  public iconsForm: FormGroup = this.formBuilder.group({});

  public adminMenuIcons: WritableSignal<CustomerMenuResource | null> = signal(null);
  public userMenuIcons: WritableSignal<UserMenuResource | null> = signal(null);
  public needsToBeSaved: WritableSignal<boolean> = signal(false);

  //TODO: 1. the layout now allows isAdminSettings=true (Admin accessing this dialog from the Settings component) to save Desktop and Mobile icon visibility separately. This should be implemented here and in the backend.
  //TODO: 2. Also if the dialog is isAdminSettings=false, this dialog will be displayed for the user and they can save their own order and hide icons, BUT ONLY THOSE WHICH THE ADMIN MADE AVAILABLE. This should be saved in the users side of settings and not in the global settings.
  //TODO: 3. the Settings icon should not be in the list, because the user can disable it and then he will not be able to access it anymore, locking himself out of app wide changes

  private inputAttributeEffect: EffectRef = effect(() => {
    if(this.visible()) {
      if(this.isAdminSettings()) {
        this.navService.loadCustomerMenu(this.profileService.profile()?.user?.language ?? 'en'); // to ensure we have the latest menu configuration after potential changes in the admin menu
      } else {
        this.navService.reloadMenus(); // to ensure we have the latest menu configuration after potential changes in the user menu
      }
    }
  });

  public displayMenuIcons: WritableSignal<TYPE.HomeIcon[]> = linkedSignal(() => {
    if(this.isAdminSettings()) {
      return this.navService.customerMenu();
    } else {
      return this.navService.userSettingsMenu()
    }
  });

  public setWebValue(type: SettingsMenuItemType, value: boolean): void {
    const icons = this.displayMenuIcons();
    const icon = icons.find(i => i.type === type);
    if (icon && icon.is_active_desktop !== value) {
      icon.is_active_desktop = value;
      this.displayMenuIcons.set([...icons]);
      this.needsToBeSaved.set(true);
    }
  }

  public setMobileValue(type: SettingsMenuItemType, value: boolean): void {
    const icons = this.displayMenuIcons();
    const icon = icons.find(i => i.type === type);
    if (icon && icon.is_active_mobile !== value) {
      icon.is_active_mobile = value;
      this.displayMenuIcons.set([...icons]);
      this.needsToBeSaved.set(true);
    }
  }

  public close(): void {
    this.navService.resetCustomerMenu();
    this.adminMenuIcons.set(null);
    this.needsToBeSaved.set(false);
    this.visibleChange.emit(false);
  }

  public moveIcon(direction: 'up' | 'down', type: SettingsMenuItemType): void {
    const icons = [...this.displayMenuIcons()];
    const idx = icons.findIndex(i => i.type === type);
    if (idx === -1) return;
    if (direction === 'up' && idx > 0) {
      [icons[idx - 1], icons[idx]] = [icons[idx], icons[idx - 1]];
      this.displayMenuIcons.set(icons);
      this.needsToBeSaved.set(true);
    }
    if (direction === 'down' && idx < icons.length - 1) {
      [icons[idx], icons[idx + 1]] = [icons[idx + 1], icons[idx]];
      this.displayMenuIcons.set(icons);
      this.needsToBeSaved.set(true);
    }
  }

  public save(): void {
    if(this.isAdminSettings()) {
      const mappedData: SettingsMenuCustomerRequest = {
        items: this.displayMenuIcons()
        .map((icon): SettingsMenuCustomerRequestItemsInner => (
          {
            type: icon.type,
            show_on_web: !!icon.is_active_desktop,
            show_on_mobile: !!icon.is_active_mobile,
          }
        ))
      };
      this.apiService.setSettingsCustomerMenuOptions(mappedData)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.navService.reloadMenus();
          this.close();
        },
        error: (err) => {
          console.error("Error saving customer menu", err);
        }
      });
    } else {
      const mappedData: UserMenuRequest = {
        items: this.displayMenuIcons()
        .map((icon): UserMenuItemRequest => (
          {
            type: icon.type,
          }
        ))
      };
      this.apiService.setSettingsUserMenu(mappedData)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.navService.reloadMenus();
          this.close();
        },
        error: (err) => {
          console.error("Error saving user menu", err);
        }
      });
    }
  }
}
